import React, { Dispatch, FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  AlertColor,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useTheme } from '@mui/system';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HotelOutlinedIcon from '@mui/icons-material/HotelOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import KingBedOutlinedIcon from '@mui/icons-material/KingBedOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import dayjs, { Dayjs } from 'dayjs';
import strings from '../../../../../../common/Translation/Translate';
import Notification from '../../../../../../components/Notification';
import axiosInstance from '../../../../../../utils/axios';
import { URLS } from '../../../../../../utils/constants/urls';
import { identifiers } from '../../../../../../utils/constants/identifiers';
import DateInput from '../../../../../../components/DateInput/DateInput';
import PageLoader from '../../../../../../components/PageLoader';
import { getMinBookingDate } from '../../../../../../utils/validators/HelperFunctions';

// ----------------------------------------------------------------------------------------
// Types
// ----------------------------------------------------------------------------------------

/**
 * Shape of a single room row stored in the booking. Kept loose (everything optional) because
 * older persisted rooms may not carry the newer YCS/external metadata and the parent screens
 * continue to spread the object as-is when building the save payload.
 */
type Room = {
  // Populated when the row came from master-data (edit flow).
  bookedRoomId?: number;

  roomType?: number | string;
  type?: string;

  from?: string;
  to?: string;

  numberOfRooms?: number | string;
  pricePerDay?: number | string;

  mealPlanId?: number | string | null;
  mealPlanName?: string;
  mealPlanPrice?: number | string;

  /** Selected tax group id (single-select). */
  taxGroup?: number | string | null;
  taxGroupName?: string;
};

type DropdownOption = { label: string; value: number | string; price?: number };

type AddEditBookingRoomsProps = {
  rooms: Room[];
  setRooms: Dispatch<any>;
  roomGridError?: boolean;
  isCompany?: boolean;
  branchId?: string;
  pastBookingWindowDays?: number | null;
  /** Optional preloaded tax-group options; meal-plans API also supplies them. */
  taxGroups?: DropdownOption[];
};

// ----------------------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------------------

/** Parse the backend "DD-MM-YYYY" format into a dayjs object (null-safe). */
const fromDdMmYyyy = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value, 'DD-MM-YYYY');
  return parsed.isValid() ? parsed : null;
};

/** Parse DateInput's "YYYY-MM-DD" wire format into dayjs (null-safe). */
const fromYyyyMmDd = (value?: string | null): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(value, 'YYYY-MM-DD');
  return parsed.isValid() ? parsed : null;
};

const emptyForm = (): Partial<Room> => ({
  roomType: '',
  mealPlanId: '',
  mealPlanPrice: '',
  from: '',
  to: '',
  numberOfRooms: '',
  pricePerDay: '',
  taxGroup: '',
  taxGroupName: '',
});

// ----------------------------------------------------------------------------------------
// Component
// ----------------------------------------------------------------------------------------

export const AddEditBookingRooms: FC<AddEditBookingRoomsProps> = ({
  rooms,
  setRooms,
  roomGridError,
  isCompany = false,
  branchId = '',
  pastBookingWindowDays = null,
  taxGroups: taxGroupsProp = [],
}) => {
  const theme = useTheme();

  // Dropdown sources
  const [roomsList, setRoomsList] = useState<DropdownOption[]>([]);
  const [mealPlanList, setMealPlanList] = useState<DropdownOption[]>([]);
  const [taxGroupList, setTaxGroupList] = useState<DropdownOption[]>(taxGroupsProp);

  // Form state — the "compose" panel at the top of the rooms tab.
  const [form, setForm] = useState<Partial<Room>>(emptyForm());
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deleteIdx, setDeleteIdx] = useState<number | null>(null);
  const [message, setMessage] = useState<{
    display: boolean;
    severity: AlertColor | null;
    message: string;
  }>({ display: false, severity: null, message: '' });
  const formCardRef = useRef<HTMLDivElement | null>(null);

  // ----- Data loaders ---------------------------------------------------------------------

  useEffect(() => {
    if (taxGroupsProp?.length) {
      setTaxGroupList(taxGroupsProp);
    }
  }, [taxGroupsProp]);

  const getRoomsList = useCallback(async () => {
    try {
      const { data } = await axiosInstance.get(URLS.RoomTypeList);
      setRoomsList(data.map((item: any) => ({ label: item.name, value: item.id })));
    } catch (error) {
      console.error('Error fetching room types', error);
    }
  }, []);

  const getMealPlansDropdown = useCallback(
    async (roomTypeId: number | string, editedValue?: Partial<Room>) => {
      if (!roomTypeId) return;
      try {
        setLoading(true);
        const { data } = await axiosInstance.get(URLS.new_booking_mealPlansById(Number(roomTypeId), isCompany));

        setMealPlanList(
          (data?.mealPlans ?? []).map((item: any) => ({
            label: item.name,
            value: item.id,
            price: item.price,
          })),
        );

        const apiTaxGroups = (data?.taxGroups ?? []).map((item: any) => ({
          label: item.name,
          value: item.id,
        }));
        if (apiTaxGroups.length) {
          setTaxGroupList(apiTaxGroups);
        }

        const defaultTaxGroupId =
          data?.selectedTaxGroup?.value ?? data?.selectedTaxGroup?.id ?? '';

        setForm(prev => {
          const base = editedValue ? { ...editedValue } : { ...prev };
          const existingTaxGroup = editedValue?.taxGroup || prev.taxGroup;
          const taxGroup =
            existingTaxGroup !== '' && existingTaxGroup != null ? existingTaxGroup : defaultTaxGroupId;
          const taxGroupOption = (apiTaxGroups.length ? apiTaxGroups : taxGroupList).find(
            (g: DropdownOption) => String(g.value) === String(taxGroup),
          );
          return {
            ...base,
            taxGroup,
            taxGroupName:
              editedValue?.taxGroupName ||
              taxGroupOption?.label ||
              data?.selectedTaxGroup?.label ||
              prev.taxGroupName ||
              '',
            pricePerDay:
              editedValue?.pricePerDay ??
              (prev.pricePerDay !== '' && prev.pricePerDay != null ? prev.pricePerDay : data?.roomPrice ?? ''),
          };
        });
      } catch (error) {
        console.error('Error fetching meal plans', error);
      } finally {
        setLoading(false);
      }
    },
    [isCompany, taxGroupList],
  );

  const fetchCorporatePrice = useCallback(
    async (draft: Partial<Room>) => {
      if (!isCompany || !branchId) return;
      if (!draft.roomType || !draft.from || !draft.to) return;
      try {
        const req = {
          branchId,
          checkIn: draft.from,
          checkOut: draft.to,
          roomTypeId: draft.roomType,
        };
        const { data } = await axiosInstance.post(URLS.corporateRoomRates, req);
        setForm(prev => ({ ...prev, pricePerDay: data?.price ?? prev.pricePerDay }));
        if (data?.price != null && data.price < 1) {
          setMessage({
            display: true,
            severity: 'warning',
            message: data.description || strings.editBooking_room_checkIn_text,
          });
        }
      } catch (e: any) {
        setMessage({
          display: true,
          severity: 'warning',
          message: e?.response?.data?.message || 'Unable to fetch corporate rate',
        });
      }
    },
    [branchId, isCompany],
  );

  useEffect(() => {
    getRoomsList();
  }, [getRoomsList]);

  // ----- Field handlers ------------------------------------------------------------------

  /** Set a form value and, where needed, trigger side-effects (rate / meal plan refresh). */
  const setField = (field: keyof Room, value: any) => {
    setForm(prev => {
      const next: Partial<Room> = { ...prev, [field]: value };

      if (field === 'roomType') {
        // Reset meal plan pricing on room-type change so the user explicitly re-picks.
        next.mealPlanId = '';
        next.mealPlanPrice = '';
        next.taxGroup = '';
        next.taxGroupName = '';
        getMealPlansDropdown(value);
      }
      if (field === 'mealPlanId') {
        const match = mealPlanList.find(m => m.value === value);
        next.mealPlanPrice = match?.price ?? '';
      }
      if (field === 'taxGroup') {
        const match = taxGroupList.find(g => String(g.value) === String(value));
        next.taxGroupName = match?.label ?? '';
      }

      if (
        isCompany &&
        branchId &&
        (field === 'roomType' || field === 'from' || field === 'to') &&
        next.roomType &&
        next.from &&
        next.to
      ) {
        // Async corporate rate lookup — fire and forget, errors surface as notifications.
        fetchCorporatePrice(next);
      }

      return next;
    });
  };

  // ----- Validation ----------------------------------------------------------------------

  type Errors = Partial<Record<keyof Room, string>>;
  const errors: Errors = useMemo(() => {
    const err: Errors = {};
    if (!form.roomType) err.roomType = 'Required';
    if (!form.from) err.from = 'Required';
    if (!form.to) err.to = 'Required';
    if (form.from && form.to) {
      const f = fromYyyyMmDd(form.from as string);
      const t = fromYyyyMmDd(form.to as string);
      if (f && t) {
        if (t.isBefore(f)) err.to = strings.editBooking_room_date_validation;
        else if (t.isSame(f)) err.to = strings.editBooking_room_date_validationForSame;
      }
    }
    const numRooms = Number(form.numberOfRooms);
    if (form.numberOfRooms === '' || form.numberOfRooms == null || isNaN(numRooms) || numRooms < 1) {
      err.numberOfRooms = 'Enter a valid number';
    }
    const price = Number(form.pricePerDay);
    if (form.pricePerDay === '' || form.pricePerDay == null || isNaN(price) || price < 0) {
      err.pricePerDay = 'Enter a valid price';
    }
    if (form.taxGroup === '' || form.taxGroup == null) err.taxGroup = 'Required';
    return err;
  }, [form]);

  const isValid = Object.keys(errors).length === 0;

  // ----- Actions -------------------------------------------------------------------------

  const resetForm = () => {
    setForm(emptyForm());
    setEditingIndex(null);
    setShowErrors(false);
    setMealPlanList([]);
  };

  const handleSubmit = () => {
    if (!isValid) {
      setShowErrors(true);
      return;
    }
    const chosenRoom = roomsList.find(r => r.value === form.roomType);
    const chosenMeal = mealPlanList.find(m => m.value === form.mealPlanId);
    const chosenTaxGroup = taxGroupList.find(g => String(g.value) === String(form.taxGroup));

    const newRoom: Room = {
      ...form,
      numberOfRooms: Number(form.numberOfRooms),
      pricePerDay: Number(form.pricePerDay),
      mealPlanPrice: form.mealPlanPrice !== '' && form.mealPlanPrice != null ? Number(form.mealPlanPrice) : 0,
      // Persist dates in the DD-MM-YYYY format used by the booking payload.
      from: dayjs(form.from as string).format('DD-MM-YYYY'),
      to: dayjs(form.to as string).format('DD-MM-YYYY'),
      type: chosenRoom?.label,
      mealPlanName: chosenMeal?.label,
      taxGroup: form.taxGroup,
      taxGroupName: chosenTaxGroup?.label || form.taxGroupName,
    };

    setRooms((prev: Room[]) => {
      const next = [...(prev || [])];
      if (editingIndex != null) {
        next[editingIndex] = { ...next[editingIndex], ...newRoom };
      } else {
        next.push(newRoom);
      }
      return next;
    });

    setMessage({
      display: true,
      severity: 'success',
      message: editingIndex != null ? 'Room updated.' : 'Room added.',
    });
    resetForm();
  };

  const handleEdit = (idx: number) => {
    const current = rooms[idx];
    if (!current) return;
    setEditingIndex(idx);
    setShowErrors(false);

    // Convert DD-MM-YYYY → YYYY-MM-DD for DateInput compatibility.
    const taxGroupValue =
      (current as any).taxGroup?.value ??
      (current as any).taxGroup ??
      '';
    const taxGroupName =
      (current as any).taxGroup?.label ??
      current.taxGroupName ??
      '';

    const prefill: Partial<Room> = {
      ...current,
      from: fromDdMmYyyy(current.from)?.format('YYYY-MM-DD') ?? '',
      to: fromDdMmYyyy(current.to)?.format('YYYY-MM-DD') ?? '',
      taxGroup: taxGroupValue,
      taxGroupName,
    };

    setForm(prefill);

    if (current.roomType) {
      getMealPlansDropdown(current.roomType, prefill);
    }

    // Smooth-scroll the compose panel into view so the user sees the form update.
    setTimeout(() => {
      formCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const handleDelete = (idx: number) => setDeleteIdx(idx);

  const confirmDelete = () => {
    if (deleteIdx == null) return;
    setRooms((prev: Room[]) => (prev || []).filter((_: Room, i: number) => i !== deleteIdx));
    if (editingIndex === deleteIdx) resetForm();
    else if (editingIndex != null && editingIndex > deleteIdx) setEditingIndex(editingIndex - 1);
    setDeleteIdx(null);
  };

  // ----- Derived data for the UI ---------------------------------------------------------

  const mealPlanDisabled = !form.roomType || mealPlanList.length === 0;

  const today = useMemo(() => getMinBookingDate(pastBookingWindowDays), [pastBookingWindowDays]);

  // ----- Render --------------------------------------------------------------------------

  return (
    <Box>
      {loading && <PageLoader />}
      {message.display && (
        <Notification
          isOpen={message.display}
          message={message.message}
          severity={message.severity as AlertColor}
          closeNotification={v => setMessage(m => ({ ...m, display: v }))}
          autoHideDuration={2000}
        />
      )}

      {/* ====================== Compose panel (replaces the old drawer) ====================== */}
      <Paper
        ref={formCardRef}
        elevation={0}
        sx={{
          p: { xs: 2, md: 3 },
          mb: 2,
          borderRadius: 3,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)} 0%, ${alpha(
            theme.palette.primary.light,
            0.04,
          )} 100%)`,
        }}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 2 }}>
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Avatar
              variant="rounded"
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.12),
                color: 'primary.main',
                width: 40,
                height: 40,
              }}>
              {editingIndex != null ? <EditOutlinedIcon /> : <AddCircleOutlineIcon />}
            </Avatar>
            <Box>
              <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
                {editingIndex != null ? strings.editRoom : strings.AddNewRoom}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editingIndex != null
                  ? 'Update the fields below and save to apply your changes.'
                  : 'Fill the details below and add as many rooms as you need.'}
              </Typography>
            </Box>
          </Stack>
          {editingIndex != null && (
            <Button size="small" variant="text" color="inherit" startIcon={<CloseRoundedIcon />} onClick={resetForm}>
              Cancel edit
            </Button>
          )}
        </Stack>

        {isCompany && !branchId && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Select a {strings.sales_company_branch_name} first to unlock corporate room rates.
          </Alert>
        )}

        {/*
         * Field layout (2 × 4 on desktop, stacks gracefully on narrower screens):
         *   Row 1: Check-in · Check-out · Room type · Meal plan
         *   Row 2: # Rooms  · Price     · Meal plan price · Tax group
         */}
        <Grid container spacing={2}>
          {/* Check-in */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%' } }}>
              <DateInput
                field="from"
                label={strings.editBooking_room_checkIn_text}
                inputFormat={identifiers.date_format}
                value={fromYyyyMmDd(form.from as string)}
                minDate={today}
                textChange={(val: string) => setField('from', val)}
                onChange={() => {
                  /* handled by textChange */
                }}
                renderInput={() => <TextField size="medium" />}
                hasError={showErrors}
                fieldError={errors.from as any}
                validators={[] as any}
              />
            </Box>
          </Grid>

          {/* Check-out */}
          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ '& .MuiFormControl-root, & .MuiTextField-root': { width: '100%' } }}>
              <DateInput
                field="to"
                label={strings.editBooking_room_checkOut_text}
                inputFormat={identifiers.date_format}
                value={fromYyyyMmDd(form.to as string)}
                minDate={fromYyyyMmDd(form.from as string) ?? today}
                textChange={(val: string) => setField('to', val)}
                onChange={() => {
                  /* handled by textChange */
                }}
                renderInput={() => <TextField size="medium" />}
                hasError={showErrors}
                fieldError={errors.to as any}
                validators={[] as any}
              />
            </Box>
          </Grid>

          {/* Room type */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth error={showErrors && !!errors.roomType} disabled={isCompany && !branchId}>
              <InputLabel>{strings.bookingDetailsRoomType} *</InputLabel>
              <Select
                label={`${strings.bookingDetailsRoomType} *`}
                value={form.roomType ?? ''}
                onChange={e => setField('roomType', e.target.value)}>
                {roomsList.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {showErrors && errors.roomType && <FormHelperText>{errors.roomType}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Meal plan */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth disabled={mealPlanDisabled}>
              <InputLabel>{strings.bookingDetailsMealPlan}</InputLabel>
              <Select
                label={strings.bookingDetailsMealPlan}
                value={form.mealPlanId ?? ''}
                onChange={e => setField('mealPlanId', e.target.value)}>
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {mealPlanList.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                    {opt.price != null ? ` — ${opt.price}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Number of rooms */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label={`${strings.bookingDetailsNumberOfRooms} *`}
              value={form.numberOfRooms ?? ''}
              onChange={e => setField('numberOfRooms', e.target.value)}
              inputProps={{ min: 1, max: 10000 }}
              error={showErrors && !!errors.numberOfRooms}
              helperText={showErrors ? errors.numberOfRooms : ''}
            />
          </Grid>

          {/* Price per day */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label={`${strings.price} *`}
              value={form.pricePerDay ?? ''}
              onChange={e => setField('pricePerDay', e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              error={showErrors && !!errors.pricePerDay}
              helperText={showErrors ? errors.pricePerDay : ''}
            />
          </Grid>

          {/* Meal plan price */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              type="number"
              label={strings.bookingDetailsMealPlanPrice}
              value={form.mealPlanPrice ?? ''}
              onChange={e => setField('mealPlanPrice', e.target.value)}
              inputProps={{ min: 0, step: 0.01 }}
              disabled={!form.mealPlanId}
            />
          </Grid>

          {/* Tax group (single-select) */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth error={showErrors && !!errors.taxGroup} disabled={!form.roomType}>
              <InputLabel>{`${strings.room_text_taxGroup} *`}</InputLabel>
              <Select
                label={`${strings.room_text_taxGroup} *`}
                value={form.taxGroup ?? ''}
                onChange={e => setField('taxGroup', e.target.value)}>
                {taxGroupList.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
              {showErrors && errors.taxGroup && <FormHelperText>{errors.taxGroup}</FormHelperText>}
            </FormControl>
          </Grid>
        </Grid>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end" sx={{ mt: 2 }}>
          {editingIndex != null && (
            <Button variant="text" onClick={resetForm} startIcon={<CloseRoundedIcon />}>
              Discard
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            startIcon={editingIndex != null ? <CheckRoundedIcon /> : <AddCircleOutlineIcon />}
            sx={{ color: '#fff', px: 3 }}
            data-testid="addRoom">
            {editingIndex != null ? 'Update room' : strings.AddNewRoom}
          </Button>
        </Stack>
      </Paper>

      {/* ============================== Added rooms list ============================== */}
      {(rooms?.length ?? 0) > 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, md: 2 },
            borderRadius: 3,
            border: `1px solid ${alpha(theme.palette.divider, 1)}`,
          }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5, px: 0.5 }}>
            <Stack direction="row" spacing={1.25} alignItems="center">
              <HotelOutlinedIcon color="primary" />
              <Typography sx={{ fontSize: 15, fontWeight: 700 }}>{strings.bookingDetails_booking_rooms}</Typography>
              <Chip size="small" label={rooms.length} color="primary" variant="outlined" />
            </Stack>
          </Stack>

          <Stack spacing={1.25}>
            {rooms.map((room: Room, idx: number) => {
              const from = fromDdMmYyyy(room.from);
              const to = fromDdMmYyyy(room.to);
              const nights = from && to ? Math.max(0, to.diff(from, 'day')) : 0;
              const qty = Number(room.numberOfRooms) || 0;
              const pricePerDay = Number(room.pricePerDay) || 0;
              const mealPlanPrice = Number(room.mealPlanPrice) || 0;
              const lineTotal = (pricePerDay + mealPlanPrice) * nights * qty;
              const isEditing = editingIndex === idx;
              const hasError = roomGridError && (room.from == null || room.to == null);

              return (
                <Paper
                  key={room.bookedRoomId ?? `${room.type}-${idx}`}
                  variant="outlined"
                  sx={{
                    p: 1,
                    borderRadius: 2,
                    borderStyle: 'dashed',
                    borderColor: hasError
                      ? theme.palette.error.main
                      : isEditing
                      ? alpha(theme.palette.primary.main, 0.6)
                      : alpha(theme.palette.divider, 0.5),
                    bgcolor: isEditing ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
                  }}>
                  <Grid container spacing={1.25} alignItems="center">
                    <Grid item xs={12} md={5}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Avatar
                          variant="rounded"
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: 'primary.main',
                            width: 36,
                            height: 36,
                          }}>
                          <KingBedOutlinedIcon sx={{ fontSize: 20 }} />
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                            {room.type || strings.bookingDetailsRoomType}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {room.mealPlanName
                              ? `${room.mealPlanName}${mealPlanPrice ? ` · ${mealPlanPrice}/day` : ''}`
                              : 'No meal plan'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={4}>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {from && to ? (
                          <Chip
                            size="small"
                            variant="filled"
                            sx={{
                              bgcolor: alpha(theme.palette.info.main, 0.1),
                              color: 'info.main',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              height: 24,
                            }}
                            icon={<EventAvailableOutlinedIcon sx={{ fontSize: 12 }} />}
                            label={`${from.format('DD MMM')} → ${to.format('DD MMM YYYY')}`}
                          />
                        ) : (
                          <Chip
                            size="small"
                            color="error"
                            variant="filled"
                            sx={{ fontSize: '0.7rem', fontWeight: 600, height: 24 }}
                            label="Missing dates"
                          />
                        )}
                        <Chip
                          size="small"
                          variant="filled"
                          sx={{
                            bgcolor: alpha(theme.palette.info.main, 0.1),
                            color: 'info.main',
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            height: 24,
                          }}
                          icon={<KingBedOutlinedIcon sx={{ fontSize: 12 }} />}
                          label={`${qty} room${qty === 1 ? '' : 's'} · ${nights} night${nights === 1 ? '' : 's'}`}
                        />
                        {pricePerDay > 0 && (
                          <Chip
                            size="small"
                            variant="filled"
                            sx={{
                              bgcolor: alpha(theme.palette.success.main, 0.1),
                              color: 'success.main',
                              fontSize: '0.7rem',
                              height: 24,
                            }}
                            icon={<PaidOutlinedIcon sx={{ fontSize: 12 }} />}
                            label={`${pricePerDay}/day`}
                          />
                        )}
                      </Stack>
                    </Grid>

                    <Grid item xs={12} md={3}>
                      <Stack
                        direction="row"
                        justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                        alignItems="center"
                        spacing={0.75}>
                        {lineTotal > 0 && (
                          <Box sx={{ textAlign: 'right', mr: 0.5 }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1 }}>
                              Line total
                            </Typography>
                            <Typography sx={{ fontWeight: 800, color: 'primary.main', lineHeight: 1.1 }}>
                              {lineTotal.toFixed(2)}
                            </Typography>
                          </Box>
                        )}
                        <Tooltip title={strings.editRoom}>
                          <IconButton size="small" color="primary" onClick={() => handleEdit(idx)}>
                            <EditOutlinedIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Remove room">
                          <IconButton size="small" color="error" onClick={() => handleDelete(idx)}>
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </Grid>
                  </Grid>
                </Paper>
              );
            })}
          </Stack>
        </Paper>
      ) : (
        <Paper
          elevation={0}
          sx={{
            p: 4,
            borderRadius: 3,
            border: `1px dashed ${alpha(theme.palette.divider, 1)}`,
            textAlign: 'center',
            color: 'text.secondary',
          }}>
          <HotelOutlinedIcon sx={{ fontSize: 32, opacity: 0.6, mb: 1 }} />
          <Typography variant="body2">
            No rooms added yet. Fill the form above and click <strong>{strings.AddNewRoom}</strong> to start building
            this booking.
          </Typography>
        </Paper>
      )}

      <Dialog open={deleteIdx !== null} onClose={() => setDeleteIdx(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remove room?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will remove the selected room from this booking draft. You can add it back later before saving.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteIdx(null)}>Keep</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// Export for compat with anything that imports the default.
export default AddEditBookingRooms;
